import type {ReactNode} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Clock, Mail, Smartphone} from 'lucide-react';
import {VERIFICATION_METHODS, type VerificationMethod} from '@chekin/api';
import {HighlightedTitle} from '../../../components/PageLayoutHeader';
import {VerificationStep} from './constants';

export type ContactsCopy = {
  title: ReactNode;
  intro: ReactNode;
  note: ReactNode;
  noteIcon: ReactNode;
  submitLabel: string;
  changeLabel: string;
  codeMismatch: string;
};

type Args = {
  channel: VerificationMethod;
  step: VerificationStep;
  isPreviousVerified: boolean;
  maskedTarget: string;
};

const CODE_EXPIRY_MINUTES = 2;

export function useContactsCopy({
  channel,
  step,
  isPreviousVerified,
  maskedTarget,
}: Args): ContactsCopy {
  const {t} = useTranslation();
  const isEmail = channel === VERIFICATION_METHODS.email;
  const isCodeStep = step === VerificationStep.CODES_VERIFICATION;

  const changeLabel = isEmail ? t('change_email') : t('change_phone_number');
  const codeMismatch = isEmail
    ? t('code_did_not_match_email')
    : t('code_did_not_match_sms');

  if (isCodeStep) {
    return {
      title: (
        <HighlightedTitle
          i18nKey={isEmail ? 'check_your_email_title' : 'check_your_messages_title'}
        />
      ),
      intro: (
        <Trans
          i18nKey={isEmail ? 'code_sent_to_email' : 'code_sent_to_sms'}
          values={{target: maskedTarget}}
          components={{
            1: <b className="font-semibold text-[var(--chekin-color-brand-navy)]" />,
          }}
        />
      ),
      note: (
        <Trans
          i18nKey="code_expires_in_minutes"
          values={{minutes: CODE_EXPIRY_MINUTES}}
          components={{1: <b />}}
        />
      ),
      noteIcon: <Clock />,
      submitLabel: t('verify_code'),
      changeLabel,
      codeMismatch,
    };
  }

  return {
    title: (
      <HighlightedTitle
        i18nKey={isEmail ? 'verify_your_email_title' : 'verify_your_phone_title'}
      />
    ),
    intro: isEmail
      ? t('verify_email_intro')
      : isPreviousVerified
      ? t('verify_phone_intro_after_email')
      : t('verify_phone_intro'),
    note: (
      <Trans
        i18nKey={isEmail ? 'code_delivery_by_email' : 'code_delivery_by_sms'}
        components={{1: <b />}}
      />
    ),
    noteIcon: isEmail ? <Mail /> : <Smartphone />,
    submitLabel: t('send_verification_code'),
    changeLabel,
    codeMismatch,
  };
}
